/**
 * RBAC Feature Codes
 * These match the backend feature codes exactly
 */
export const FEATURE_CODES = {
  // Primary Page Features
  DIGITAL_TWIN: 'DIGITAL_TWIN', // Supports: view, create, update (add/edit digital twins, chassis number editing)
  DIGITAL_TWIN_CONFIG: 'DIGITAL_TWIN_CONFIG', // Supports: view, create, update (battery configurations)
  RESOURCE_ONBOARDING: 'RESOURCE_ONBOARDING', // Supports: view, create (parent gate: page access + New Upload button)
  // Per-import-type gates. The controlling featureCode is declared on each
  // dataImportTypes row (controlledByFeatureCode); these constants are for reference.
  RESOURCE_ONBOARDING_IOT: 'RESOURCE_ONBOARDING_IOT', // Supports: view, create
  RESOURCE_ONBOARDING_INVENTORY: 'RESOURCE_ONBOARDING_INVENTORY', // Supports: view, create
  DATA_DOWNLOAD: 'DATA_DOWNLOAD', // Supports: view, create
  IOT_DEVICES: 'IOT_DEVICES', // Supports: view only (IOT listing tab in inventory)
  IOT_DEVICE_TESTING: 'IOT_DEVICE_TESTING', // Gates the testing actions (Start/Re-test/Pass/Fail/View) in the Unmapped IOT Devices tab
  FLEET_PERFORMANCE: 'FLEET_PERFORMANCE', // Supports: view
  // Action Features
  OPERATIONAL_STATUS: 'OPERATIONAL_STATUS', // Supports: update (change operational status of batteries)
  BMS_FAULTS_V2: 'BMS_FAULTS_V2', // Supports: view (access BMS Faults page + Live Issue Stream), update (update alert status/comments), force_resolve (force-resolve alerts)
  PLATFORM_ALERTS_V2_SETTINGS: 'PLATFORM_ALERTS_V2_SETTINGS', // Supports: view (access alert settings page), create (add new alert), update (edit alert config), delete (remove alert)
  PLATFORM_ALERTS_V2: 'PLATFORM_ALERTS_V2', // Supports: view, update (update alert status/comments), force_resolve (force-resolve alerts)
  // Admin Features
  ACCESS_MANAGEMENT: 'ACCESS_MANAGEMENT', // Supports: view (page access), create (add user), update (edit access / force logout)
  // Org Settings Features
  ORG_SETTINGS: 'ORG_SETTINGS', // Supports: view (parent gate for the Org Settings sidebar group)
  SUPPORTED_LOCATIONS: 'SUPPORTED_LOCATIONS', // Supports: view, create, update, delete
  CLIENT_API_MANAGEMENT: 'CLIENT_API_MANAGEMENT', // Supports: view only (read-only API key listing)

  BATTERY_INTELLIGENCE : "BATTERY_INTELLIGENCE",
  BATTERY_COMPONENTS: 'BATTERY_COMPONENTS', // Supports: view, create, update, delete (Digital Twin Components tab: pack replacement & tracking)
  // Filter Features
  GLOBAL_ADVANCED_FILTER: 'GLOBAL_ADVANCED_FILTER', // Supports: view (enables new hierarchical location + fleet filter)
  // Dashboard Platform Features
  COULOMB_OBSERVABILITY_PLATFORM: 'COULOMB_OBSERVABILITY_PLATFORM', // Supports: view (access to Coulomb dashboard)
  INDIGO_OBSERVABILITY_DASHBOARD: 'INDIGO_OBSERVABILITY_DASHBOARD', // Supports: view (access to Indigo custom dashboard)
  INDIGO_OBSERVABILITY_PLATFORM_BMS_ALERTS: 'INDIGO_OBSERVABILITY_PLATFORM.BMS_ALERTS', // Supports: view (BMS Faults visibility in custom dashboard),
  ASSET_MANAGEMENT_NETWORK_ENTITIES: 'ASSET_MANAGEMENT.NETWORK_ENTITIES', // Supports: view, create, update, delete (delete is approver-only)
  ASSET_MANAGEMENT_ASSETS: 'ASSET_MANAGEMENT.ASSETS', // Supports: view, create, update, delete
  ASSET_MANAGEMENT_WORK_ORDERS: 'ASSET_MANAGEMENT.WORK_ORDERS', // Supports: view (the queue and every read; acting on a work order is granted per type below)
};

// Per work order type, in its OWN namespace: WORK_ORDER.<TYPE>, where <TYPE> is
// workOrderType.code upper-cased. `create` on it lets a user raise that kind of
// work order and makes them its maker; `approve` lets them sign one off and makes
// them its checker.
//
// Deliberately NOT nested under ASSET_MANAGEMENT.WORK_ORDERS. Seeing the work
// order queue and being allowed to approve a pack replacement are different
// authorities, usually held by different people; nesting made them read as one
// thing with a suffix. Dotted codes do not inherit in Casbin anyway, so a grant
// on either parent gives nothing here -- which is what keeps a dispatch approver
// out of pack replacements.
export const WORK_ORDER_TYPE_FEATURE_PREFIX = 'WORK_ORDER';

export const workOrderFeatureCode = (workOrderTypeCode) =>
  `${WORK_ORDER_TYPE_FEATURE_PREFIX}.${String(workOrderTypeCode || '').toUpperCase()}`;

/**
 * Route to Feature Code Mapping
 * Maps frontend routes to RBAC feature codes
 * Note: /inventory is not protected at route level - tabs are filtered internally based on permissions
 */
export const ROUTE_FEATURE_MAP = {
  '/resource-onboarding': FEATURE_CODES.RESOURCE_ONBOARDING,
  '/data-download': FEATURE_CODES.DATA_DOWNLOAD,
  '/fleet-performance': FEATURE_CODES.FLEET_PERFORMANCE,
  '/bms-faults': FEATURE_CODES.BMS_FAULTS_V2,
  '/battery-intelligence' : FEATURE_CODES.BATTERY_INTELLIGENCE,
  '/platform-alerts-v2': FEATURE_CODES.PLATFORM_ALERTS_V2,
  '/access-management': FEATURE_CODES.ACCESS_MANAGEMENT,
  '/access-management/roles': FEATURE_CODES.ACCESS_MANAGEMENT,
  '/access-management/users': FEATURE_CODES.ACCESS_MANAGEMENT,
  '/org-settings/supported-locations': FEATURE_CODES.SUPPORTED_LOCATIONS,
  '/org-settings/client-api-management': FEATURE_CODES.CLIENT_API_MANAGEMENT,
  // An ARRAY means any-of: the page opens if the user holds view on at least
  // one of them. Asset Management is three independent tabs behind three
  // features, and a user granted only Assets must still be able to reach the
  // page -- gating the route on Network alone locked them out of a module they
  // had been given. Casbin cannot express this for us: dotted feature codes do
  // not inherit, so there is no parent grant that would cover all three
  // without hand-written g2 rows per environment.
  '/asset-management': [
    FEATURE_CODES.ASSET_MANAGEMENT_NETWORK_ENTITIES,
    FEATURE_CODES.ASSET_MANAGEMENT_ASSETS,
    FEATURE_CODES.ASSET_MANAGEMENT_WORK_ORDERS,
  ],
};

/**
 * Old to New Permission Mapping
 * For backward compatibility during migration
 */
export const OLD_TO_NEW_PERMISSION_MAP = {
  'view-twin': { feature: FEATURE_CODES.DIGITAL_TWIN, operation: 'view' },
  'add-twin': { feature: FEATURE_CODES.DIGITAL_TWIN, operation: 'create' },
  'edit-twin': { feature: FEATURE_CODES.DIGITAL_TWIN, operation: 'update' },
};

/**
 * Operation Types
 */
export const OPERATIONS = {
  VIEW: 'view',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  FORCE_RESOLVE: 'force_resolve',
  // DIGITAL_TWIN only: change a vehicle's Project, Protocol and Bus Type.
  CRITICAL_UPDATE: 'critical_update',
};

/**
 * The feature codes gating a route, always as an array.
 *
 * Route values are a single code or an array of them; every caller goes
 * through this so neither shape has to be handled at the call site. An unmapped
 * route returns [], which every caller reads as "no restriction" -- the
 * backward-compatible behaviour the gates already had.
 */
export const routeFeatureCodes = (route) => {
  const mapped = ROUTE_FEATURE_MAP[route];
  if (!mapped) return [];
  return Array.isArray(mapped) ? mapped : [mapped];
};
